import React, {useEffect, useRef, useState} from 'react';
import {ActivityIndicator, Platform, View} from 'react-native';
import {RFValue} from 'react-native-responsive-fontsize';
import {
  Container,
  CheckIcon,
  PaymentMethodInfo,
  PaymentTitle,
  Payment,
  BrandContainer,
  BrandImage,
  PaymentInfo,
  PromoCard,
  InstallmentHintContainer,
  InstallmentHintTitle,
  InstallmentHintSubtitle,
  InstallmentsContainer,
} from './styles';
import {UserCreditCardsDTO} from '../../../../dtos/UserCreditCardsDTO';
import {ParcelasDTO} from '../../../../dtos/ParcelasDTO';

import {useAuth} from '../../../../context/auth';

import {PedidoDTO} from '../../../../dtos/PedidoDTO';
import {useCards} from '../../../../context/cards';
import {api} from '../../../../services/api';
import {useNavigation, useRoute} from '@react-navigation/native';
import {StationDTO} from '../../../../dtos/StationDTO';
import ShimmerPlaceholder from 'react-native-shimmer-placeholder';
import LinearGradient from 'react-native-linear-gradient';
import {InstallmentItem} from '../InstallmentIntem';
import {useHelpers} from '../../../../context/helpers';
import {useTheme} from 'styled-components/native';
import { usePaymentMethod } from '../CheckoutSession/hooks/usePaymentMethod';

type PaymentMethod = 'PIX' | 'TICKETLOG' | UserCreditCardsDTO | null;

interface Props {
  pedidos: PedidoDTO | null;
  localidade?: string | null;
  selectedInstallment?: ParcelasDTO | null;
  isLoadingInstallments?: boolean;
  canInstallments?: boolean;
  onPressInstallments?: () => void;
  onSelectCard?: (item: UserCreditCardsDTO) => void;
  onInstallmentLayout?: (y: number) => void;
  pulseInstallmentKey?: number;
  isStripe?: boolean;
  selectedPaymentMethod?: PaymentMethod ;
  changePaymentMethod?: (method: PaymentMethod) => void;
  enableVale?: boolean;
  enableBrbDiscount?: boolean;
  autoSelectFirst?: boolean;
}

export function UserCreditCardList({
  pedidos,
  localidade,
  selectedInstallment,
  isLoadingInstallments = false,
  canInstallments = true,
  onPressInstallments,
  onSelectCard,
  onInstallmentLayout,
  pulseInstallmentKey,
  isStripe = false,
  enableVale,
  enableBrbDiscount,
  selectedPaymentMethod: propSelectedPaymentMethod,
  changePaymentMethod: propChangePaymentMethod,
  autoSelectFirst = false,
}: Props) {
  const theme = useTheme();
  const {user, clientId} = useAuth();
  const route = useRoute();
  const navigation = useNavigation();
  const {formatNumberToLocaleAmount} = useHelpers();
  const {userCards, fetchUserCreditCardList, isLoadingCards} = useCards();

  const {
    changePaymentMethod: globalChangePaymentMethod,
    selectedPaymentMethod: globalSelectedPaymentMethod,
  } = usePaymentMethod();

  const selectedPaymentMethod =
    propSelectedPaymentMethod ?? globalSelectedPaymentMethod;
  const changePaymentMethod =
    propChangePaymentMethod ?? globalChangePaymentMethod;
  const {isLogged} = useAuth();

  const handledCardIdRef = useRef<string | null>(null);
  const containerYRef = useRef(0);
  const [cupomBrbAplicado, setCupomBrbAplicado] = useState(false);
  const isWebview = Platform.OS === 'web';
  const isSelectedCard = typeof selectedPaymentMethod !== 'string';
  const selectedCard = isSelectedCard ? selectedPaymentMethod : undefined;

  function handleSelectCard(item: UserCreditCardsDTO) {
    handledCardIdRef.current = item.id;
    changePaymentMethod(item);
    applyBRBCardDiscount(item);
    onSelectCard?.(item);
  }

  function applyBRBCardDiscount(item: UserCreditCardsDTO) {
    if (enableBrbDiscount) {
      if (item.brb) {
        navigation.setParams({
          loading: true,
        });
        if (item.primeiraCompra) {
          fetchDiscountStations({code: 'primeiravezbrb'});
        } else {
          fetchDiscountStations({code: 'brb10'});
        }
      } else if (cupomBrbAplicado) {
        setCupomBrbAplicado(false);
        navigation.setParams({
          discount: undefined,
          coupon: undefined,
          loading: false,
        });
      }
    }
  }

  useEffect(() => {
    if (!selectedCard) {
      handledCardIdRef.current = null;
      return;
    }

    if (handledCardIdRef.current === selectedCard.id) {
      return;
    }

    handledCardIdRef.current = selectedCard.id;
    applyBRBCardDiscount(selectedCard);
  }, [selectedCard]);

  useEffect(() => {
    if (selectedPaymentMethod === 'PIX') {
      if (cupomBrbAplicado) {
        navigation.setParams({
          discount: undefined,
          coupon: undefined,
          loading: false,
        });
      }
    }
  }, [selectedPaymentMethod]);

  async function fetchDiscountStations({code}: {code: string}) {
    if (!localidade) {
      return;
    }
    if (!pedidos) {
      return;
    }

    try {
      const response = await api.post('/listaPostosDesconto', {
        codigoDesconto: code.toLowerCase().replace(' ', ''),
        estado: localidade,
        clientId,
        isWebview,
        produtos: [
          {
            id: pedidos.fuelId,
            produto: pedidos.name,
            volume: pedidos.liters,
            quantity: pedidos.quantity,
          },
        ],
      });

      if (response.data) {
        handleAddDiscount(code, response.data);
      }
    } catch (error) {
      navigation.setParams({
        discount: undefined,
        coupon: undefined,
        loading: false,
      });
    }
  }

  async function handleAddDiscount(
    codigoDesconto: string,
    stations: StationDTO[],
  ) {
    if (!localidade) {
      return;
    }
    if (!pedidos) {
      return;
    }
    if (!codigoDesconto) {
      return;
    }

    try {
      const {data} = await api.post('/descontos', {
        codigoDesconto: codigoDesconto.toLowerCase().replace(' ', ''),
        produtos: [
          {
            id: pedidos.fuelId,
            produto: pedidos.name,
            volume: pedidos.liters,
            quantity: pedidos.quantity,
          },
        ],
        regiao: localidade,
      });

      if (data) {
        navigation.setParams({
          ...route.params,
          discount: {
            code: codigoDesconto,
            stations,
            value: data,
          },
          loading: false,
        });

        setCupomBrbAplicado(true);
      }
    } catch (error) {
      navigation.setParams({
        discount: undefined,
        coupon: undefined,
        loading: false,
      });
    }
  }

  useEffect(() => {
    if (isLogged) {
      fetchUserCreditCardList({});
    }
  }, [isLogged]);

  function handleContainerLayout(event: any) {
    containerYRef.current = event.nativeEvent.layout.y;
  }

  function handleInstallmentLayout(event: any) {
    onInstallmentLayout?.(containerYRef.current + event.nativeEvent.layout.y);
  }

  const userCardsFiltered = enableVale
    ? userCards
    : userCards.filter(item => !item.voucher);

  useEffect(() => {
    if (autoSelectFirst && selectedPaymentMethod === null && userCardsFiltered.length > 0) {
      changePaymentMethod(userCardsFiltered[0]);
    }
  }, [autoSelectFirst, userCardsFiltered]);

  return (
    <Container onLayout={handleContainerLayout}>
      {isLoadingCards && user.user_id !== 'convidado' && (
        <ActivityIndicator size="small" color={theme.colors.text} style={{marginBottom: RFValue(12)}}/>
      )}

      {!isLoadingCards && user.user_id !== 'convidado' &&
        userCardsFiltered.map((item, index) => {
          const isCurrentCardSelected = selectedCard?.id === item.id;
          const isLast = index === userCardsFiltered.length - 1;

          return (
            <React.Fragment key={item.id}>
              <PaymentInfo
                activeOpacity={1}
                style={{
                  marginBottom:
                    isCurrentCardSelected && isLast ? RFValue(6) : RFValue(12),
                }}
                onPress={() => handleSelectCard(item)}>
                <Payment>
                  <BrandContainer>
                    <BrandImage
                      source={{
                        uri: item.icon,
                      }}
                    />
                  </BrandContainer>
                  <PaymentMethodInfo>
                    <PaymentTitle>
                      •••• {item.number.substring(12, 16)} (
                      {item.voucher ? 'Vale' : 'Crédito'})
                    </PaymentTitle>
                    {item.brb && enableBrbDiscount && <PromoCard>Cartão BRB</PromoCard>}
                  </PaymentMethodInfo>
                </Payment>

                <CheckIcon
                  name={isCurrentCardSelected ? 'dot-circle' : 'circle'}
                  check={isCurrentCardSelected}
                />
              </PaymentInfo>

              {isCurrentCardSelected && !isStripe && (
                <InstallmentsContainer>
                  {canInstallments && !!onPressInstallments && (
                    <InstallmentHintContainer>
                      <InstallmentHintTitle>
                        Quer parcelar essa compra?
                      </InstallmentHintTitle>
                      <InstallmentHintSubtitle>
                        Clique aqui e mude o valor das parcelas
                      </InstallmentHintSubtitle>
                    </InstallmentHintContainer>
                  )}
                  {isLoadingInstallments ? (
                    <View
                      onLayout={handleInstallmentLayout}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderWidth: 1,
                        borderColor: theme.colors.border2,
                        borderRadius: RFValue(12),
                        padding: RFValue(12),
                        paddingLeft: RFValue(22),
                        paddingRight: RFValue(8),
                      }}>
                      {/* Nx */}
                      <ShimmerPlaceholder
                        shimmerColors={theme.colors.shimmer_placeholder}
                        LinearGradient={LinearGradient}
                        style={{
                          width: RFValue(18),
                          height: RFValue(14),
                          borderRadius: RFValue(4),
                        }}
                      />
                      {/* valor/mês + valor final */}
                      <View
                        style={{
                          flex: 1,
                          marginLeft: RFValue(18),
                          gap: RFValue(5),
                        }}>
                        <ShimmerPlaceholder
                          shimmerColors={theme.colors.shimmer_placeholder}
                          LinearGradient={LinearGradient}
                          style={{
                            width: '45%',
                            height: RFValue(12),
                            borderRadius: RFValue(4),
                          }}
                        />
                        <ShimmerPlaceholder
                          shimmerColors={theme.colors.shimmer_placeholder}
                          LinearGradient={LinearGradient}
                          style={{
                            width: '60%',
                            height: RFValue(11),
                            borderRadius: RFValue(4),
                          }}
                        />
                      </View>
                      {/* chevron */}
                      <ShimmerPlaceholder
                        shimmerColors={theme.colors.shimmer_placeholder}
                        LinearGradient={LinearGradient}
                        style={{
                          width: RFValue(14),
                          height: RFValue(14),
                          borderRadius: RFValue(4),
                        }}
                      />
                    </View>
                  ) : (
                    selectedInstallment && (
                      <View onLayout={handleInstallmentLayout}>
                        <InstallmentItem
                          selected
                          onPress={
                            canInstallments && onPressInstallments
                              ? onPressInstallments
                              : () => {}
                          }
                          showArrow={canInstallments && !!onPressInstallments}
                          activeOpacity={canInstallments ? undefined : 1}
                          hideInstallmentColumn={!canInstallments}
                          alert={selectedInstallment.aviso}
                          installment={formatNumberToLocaleAmount(
                            selectedInstallment.valorMes,
                          )}
                          installmentCount={selectedInstallment.installmentCount.toString()}
                          totalValue={formatNumberToLocaleAmount(
                            selectedInstallment.valorCompraTaxa,
                          )}
                          pulseKey={pulseInstallmentKey}
                        />
                      </View>
                    )
                  )}
                </InstallmentsContainer>
              )}
            </React.Fragment>
          );
        })}
    </Container>
  );
}
